import { populatePortfolioByProductLine } from 'global/utils/portfolioHelper';
import { makeAutoObservable, runInAction } from 'mobx';
import { createContext, useContext } from 'react';
import ProposalDomainModule from 'store/modules/proposal/proposalDomainModule';
import { getCatalog } from 'api/operations/productServiceV2';
import { Catalog } from 'types/productServiceV2';
import { sortByProductLineId } from '@insurancemenu/imenu-components-lib/dist/utils/ProductLinesHelper';
import { PortfolioItemsByProductLine } from 'store/modules/proposal/types';
import { getRootStoreInstance, RootStore } from 'store/rootStore';
import { SupportedUserRoles } from '@insurancemenu/imenu-components-lib/dist/types';
import { MarketingTag } from '@insurancemenu/imenu-components-lib/dist/types/productCardsTypings';
import CartItemDelegate, { OverrideRatesRedirectParams } from './delegates/cartItemDelegate';
import ProductLineDelegate from './delegates/productLineDelegate';

interface DomainData {
  groupId: string;
  cartId: string;
  enrollmentPeriodId: string;
}

/**
 * Navigation differs per flow, so the owning page supplies it
 * and the store keeps only the data logic shared by both.
 */
export interface ProposalOverrideRatesNavigation {
  onReturnBack: () => void;
  onOverrideRates: (params: OverrideRatesRedirectParams) => void;
}

export default class ProposalOverrideRatesPageStore {
  private _rootStore: RootStore;
  private _navigation: ProposalOverrideRatesNavigation;
  private _isLoading = true;
  private _proposalDomainModule = new ProposalDomainModule();
  private _catalog: Catalog | null = null;
  private _productLineDelegates: ProductLineDelegate[] = [];
  private _domainData: DomainData = {
    groupId: '',
    cartId: '',
    enrollmentPeriodId: '',
  };

  public get marketingTags(): MarketingTag[] {
    return this._catalog?.marketingTags || [];
  }

  public get isLoading(): boolean {
    return this._isLoading;
  }

  public get userRoles(): SupportedUserRoles[] {
    return this._rootStore.userStore.userRoles;
  }

  public get productLineDelegates(): ProductLineDelegate[] {
    return this._productLineDelegates;
  }

  public get isProposalConfirmed(): boolean {
    return !!this._proposalDomainModule.confirmedProposal;
  }

  private _setLoading(loadingStatus: boolean): void {
    this._isLoading = loadingStatus;
  }

  public async getInitialData(): Promise<void> {
    this._setLoading(true);
    await this._proposalDomainModule.loadProposalDomainData(this._domainData);
    await this._getCatalog();

    runInAction(() => {
      const portfoliosByProductLine = populatePortfolioByProductLine(
        this._proposalDomainModule.proposalDomainData?.portfolios || [],
        this._catalog?.productLineAttributes || [],
        this._proposalDomainModule.proposalDomainData?.cart?.cartItems || [],
      ).sort((a, b) => {
        return sortByProductLineId(a.productLineId, b.productLineId);
      });

      this._productLineDelegates = this._convertPortfolioPerProductLine(portfoliosByProductLine);
      this._setLoading(false);
    });
  }

  private _convertPortfolioPerProductLine(
    portfoliosByProductLine: PortfolioItemsByProductLine[],
  ): ProductLineDelegate[] {
    return portfoliosByProductLine.map((portfolioByProductLine) => {
      // Create cart item delegates for each portfolio in this product line
      const cartItemDelegates = portfolioByProductLine.portfolioToCartItems.map((portfolioToCartItem) => {
        return new CartItemDelegate({
          domainParams: { ...this._domainData, productLineId: portfolioByProductLine.productLineId },
          portfolioToCartItem,
          onOverrideRates: this._navigation.onOverrideRates,
        });
      });

      // Create product line delegate with cart item delegates
      return new ProductLineDelegate({
        productLineId: portfolioByProductLine.productLineId,
        productLineAttributes: portfolioByProductLine.productLineAttributes,
        cartItemDelegates,
      });
    });
  }

  private async _getCatalog(): Promise<void> {
    const cart = this._proposalDomainModule.proposalDomainData?.cart;
    if (!cart) {
      throw new Error('Cart not found');
    }

    const res = await getCatalog(
      this._proposalDomainModule.sponsorId,
      cart.lastUpdateBrowsingCriteria.effectiveDate,
      cart.rateSegment,
      cart.lastUpdateBrowsingCriteria.state,
    );
    runInAction(() => {
      this._catalog = res.data.getCatalog;
    });
  }

  public returnBack(): void {
    this._navigation.onReturnBack();
  }

  constructor(domainData: DomainData, navigation: ProposalOverrideRatesNavigation) {
    this._rootStore = getRootStoreInstance();
    this._domainData = domainData;
    this._navigation = navigation;

    makeAutoObservable(this, {}, { autoBind: true });
  }
}

const ProposalOverrideRatesPageStoreContext = createContext(
  new ProposalOverrideRatesPageStore({} as DomainData, {} as ProposalOverrideRatesNavigation),
);

export const useProposalOverrideRatesPageStore = (): ProposalOverrideRatesPageStore => {
  return useContext(ProposalOverrideRatesPageStoreContext);
};

export { ProposalOverrideRatesPageStoreContext };
